/**
 * Factory Functions
 *
 * This file contains functions that create new instances of data models.
 * These functions provide default values and cloning utilities.
 */

import type { StationSector, MasterFlightRecord } from './models';
import { formatDateToYYYYMMDD } from './formatters';

/**
 * Creates a new empty station sector with default values
 * Used when adding new sectors to a flight
 * @returns A new StationSector with empty/default values
 */
export function makeDefaultStation(): StationSector {
  return {
    // Required fields
    departureStation: '',
    arrivalStation: '',
    std: '',
    sta: '',

    // Optional fields with defaults (use null for empty)
    passengerTerminalDep: null,
    passengerTerminalArr: null,
    utcLocalTimeVariationDep: null,
    utcLocalTimeVariationArr: null,
    cd: 0,
    ca: 0,
    iataSubTypeCode: '',
    aircraftOwner: null,
    tailNo: null,
    aircraftConfiguration: '',
    onwardFlight: null,

    // Codeshare information (per sector)
    codeshareFlightNo: [],
  };
}

/**
 * Creates a new master flight record with default values
 * Used when creating a new flight in the UI
 * @returns A new MasterFlightRecord with sensible defaults
 */
export function makeNewRecord(): MasterFlightRecord {
  const today = new Date();
  return {
    csfsKey: '',
    creationDate: today.toISOString(),
    fileName: '',
    flightOwner: '',
    flightNo: '',
    scheduleStatus: 'ACTIVE',
    startDate: formatDateToYYYYMMDD(today),
    endDate: formatDateToYYYYMMDD(today),
    frequency: '',
    stations: [makeDefaultStation()],
    codeshareFlightNo: [],
    isAdhoc: false,
    overrideSeasonValidation: false,

    // Optional fields with defaults
    bucketPath: null,
    operationalSuffix: null,
    seasonId: '',
    itineraryVarId: 1,
    freqRate: '', // Must be string (backend requirement)
    iataServiceTypeCode: '',
    arrIataSubTypeCode: null,
    depIataSubTypeCode: null,
    arrAircraftOwner: null,
    depAircraftOwner: null,
    additionalFields: {
      bilateralInformation: '',
      recordSerialNumber: '',
    },
    airlineUserRef: null,
    trackingSsm: null,
    trackingAsm: null,
    deletedAt: null,
    mesgSeqRef: null,
    creatorRef: null,
    actionId: null,
    messageType: null,
  };
}

/**
 * Creates a deep clone of a flight record for editing
 * Ensures that nested objects (stations, additionalFields) are properly cloned
 * to avoid mutating the original record
 * Also formats dates to YYYY-MM-DD for date inputs
 * @param record - The record to clone
 * @returns A deep clone of the record safe for mutation
 */
export function cloneRecordForEdit(record: MasterFlightRecord): MasterFlightRecord {
  return {
    ...record,
    // Format dates for date inputs (convert from ISO string to YYYY-MM-DD)
    startDate: record.startDate ? formatDateToYYYYMMDD(record.startDate) : '',
    endDate: record.endDate ? formatDateToYYYYMMDD(record.endDate) : '',
    stations: record.stations.map(s => ({
      ...s,
      // Ensure codeshareFlightNo array is properly cloned for each sector
      codeshareFlightNo: s.codeshareFlightNo ? [...s.codeshareFlightNo] : [],
    })),
    additionalFields: record.additionalFields ? { ...record.additionalFields } : undefined,
    codeshareFlightNo: record.codeshareFlightNo ? [...record.codeshareFlightNo] : [],
  };
}
